import moment from 'moment';
import { API_URL } from '../config';
import { checkFieldInFormSection } from '../library';
import { makeApiCall } from '@Redux/actions/common';
import { phone_length_by_country } from './phone-validation';
import { beneSchemaValidation } from './beneSchemaValidation';
import { bank_validations } from './bank-validation';
import {
  r_maxLength,
  r_lessThan,
  r_onlyCaps,
  r_password,
  r_upperCase,
  r_greaterThan,
  r_onlyNumbers,
  r_phoneNumber,
  r_alphaNumeric,
  r_mobileNumber,
  r_maxLengthWith2DigitsAfterDecimal,
  normalizeAll,
  no_spaces,
  normalizeCurrency,
  dynamic_normalizer
} from './restrictions';
import showInvoiceNumberAndDates from './invoiceFieldsVaidation';

export const required = (value) => (!(value || '') ? 'Required' : undefined);
export const nameValidation = (value) => {
  let newVal = value || '';
  let validation = isRequired ? required(value) : null;
  !validation && (validation = minLength2(newVal));
  !validation && (validation = alphaWithDotAndSpaceAndHyphen(newVal));
  return validation && `Min 2 letters, special characters & numbers not allowed`;
};

// Regex for detecting special characters in names (allows letters, spaces, dots, hyphens)
const BENE_NAME_SPECIAL_CHAR_REGEX = /[^A-Za-z\s.\-]/;
// Regex to check minimum 2 consecutive alphabetic characters (prevents initials like "A.", "A-", "A B")
const BENE_NAME_MIN_LENGTH_REGEX = /[A-Za-z]{2,}/;

export const fullNameValidation = (value) => {
  const trimmedValue = (value || '').trim();

  // Condition 1: Empty → Required
  if (!trimmedValue) {
    return 'Required';
  }

  // Condition 3: Special characters present
  if (BENE_NAME_SPECIAL_CHAR_REGEX.test(trimmedValue)) {
    return 'Please enter a valid name without any special characters.';
  }

  // Split name into parts
  const parts = trimmedValue.split(/\s+/).filter(Boolean);
  const firstName = parts[0] || '';
  const lastName = parts.length > 1 ? parts[parts.length - 1] : '';

  // Condition 2: First Name less than 2 characters
  if (!BENE_NAME_MIN_LENGTH_REGEX.test(firstName)) {
    return 'Please enter the full name. Initials are not allowed.';
  }

  // Condition 4: Last Name present but less than 2 characters
  if (lastName && !BENE_NAME_MIN_LENGTH_REGEX.test(lastName)) {
    return 'Please enter the full name. Initials are not allowed.';
  }

  // Condition 5: Last Name missing → not a blocking error (handled as help text in component)
  return undefined;
};

export const uptoHundred = (value) =>
  !(value && value <= 100) ? 'Should be less then or equal to 100' : undefined;
export const requiredArray = (value) => (!value || value.length <= 0 ? 'Required' : undefined);
export const emailRequired = (value) => (value ? undefined : 'Please enter your email ID');
export const maxLength = (max) => (value) =>
  value && value.length > max ? `Must be ${max} characters or less` : undefined;

export const requiredradio = (value) => {
  return value === true || value === false || (value && value.length > 0) ? undefined : 'Required';
};
export const maxLength6 = maxLength(6);
export const maxLength15 = maxLength(15);
export const maxLength20 = maxLength(20);
export const maxLength25 = maxLength(25);
export const maxLength30 = maxLength(30);
export const maxLength40 = maxLength(40);
export const maxLength50 = maxLength(50);
export const maxLength32 = maxLength(32);
export const maxLength200 = maxLength(200);
export const maxLength255 = maxLength(255);

export const minLength = (min, fieldType) => (value) => {
  const trimmedValue = value ? value.trim() : '';
  return value && trimmedValue.length < min
    ? fieldType === 'state'
      ? 'Please enter full name of the state'
      : `Must be ${min} characters or more`
    : undefined;
};
export const minLength1 = minLength(1);
export const minLength2 = minLength(2);
export const minLength5 = minLength(5);
export const minLength8 = minLength(8);
export const minLength10 = minLength(10);
export const minLength3 = minLength(3);
export const minLength6 = minLength(6);
export const verifyIndianPincode = (value) =>
  value && value.length !== 6 ? 'Must be 6 digits' : undefined;

export const number = (value) =>
  /^$|^[0-9]+$/g.test(value || '') ? undefined : 'Must be a number';

const minValue = (min) => (value) => value && value < min ? `Must be at least ${min}` : undefined;
export const minValue18 = minValue(18);

export const email = (value) =>
  value &&
  // eslint-disable-next-line max-len
  !/^$|^(([^<>()[\]{}/`~#$%&^*=?|!\\.,;:\s@"-]+([._-][^<>()[\]{}/`~#$%&^*=?|!\\._,;:\s@"-]+)*)|(".+"))@((?!-)[^}@]+)(((\[)?[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}(\])?)|(([a-zA-Z0-9-]+\.)+[a-zA-Z1]{2,}))$/i.test(
    value || ''
  )
    ? 'Please Enter a Valid Email ID'
    : undefined;

export const verifyMobileNumber = (mobileNumber) => {
  if (mobileNumber) {
    return /^[1-9]\d{7,}$/g.test(mobileNumber)
      ? undefined
      : 'Your mobile number must contain at least 8 digits';
  }
  return undefined;
};

export const alphaWithDotAndSpace = (value) =>
  /^$|^([a-zA-Z\s.]*)$/i.test(value || '')
    ? undefined
    : 'only alphabets, dots and spaces are allowed';

export const alphaWithDotAndSpaceAndHyphen = (value) =>
  /^$|^([a-zA-Z\s.-]*)$/i.test(value || '')
    ? undefined
    : 'only alphabets, dots and spaces are allowed';

export const alphanumeric = (value) =>
  /^$|^[a-zA-Z0-9]*$/i.test(value || '') ? undefined : 'Only alphanumeric characters';

export const alphanumericWithDotAndSpace = (value) =>
  /^$|^([a-zA-Z\s]*)(\.)?([a-zA-Z0-9\s.]*)$/i.test(value || '')
    ? undefined
    : 'alphanumeric, periods and spaces are allowed';

export const alphanumericWithDotAndSpaceAndParenthesisAndAmpersand = (value) =>
  /^$|^([a-zA-Z\s]*)(\.)?([a-zA-Z0-9\s.()&]*)$/i.test(value || '')
    ? undefined
    : 'alphanumeric, periods, spaces, () and & are allowed';

export const allString = (value) =>
  /^$|^([a-zA-Z0-9()[\]{};'"\\|,.\s]*)$/i.test(value || '') ? undefined : 'Please enter valid name';

export const alphanumericWithDot = (value) =>
  /^$|^[a-zA-Z0-9.]*$/i.test(value || '') ? undefined : 'Only alphanumeric characters';

export const alphanumericWithSpace = (value) =>
  /^$|^[a-zA-Z0-9][a-zA-Z0-9 ]*$/i.test(value || '') ? undefined : 'Only alphanumeric characters';

export const alphanumericWithSpaceAndDot = (value) =>
  /^$|^[a-zA-Z0-9][a-zA-Z0-9. ]*$/i.test(value || '')
    ? undefined
    : 'Only alphanumeric characters, spaces and dots';

export const alphanumericWithSpaceDotAndHyphen = (value) =>
  /^$|^[a-zA-Z0-9][a-zA-Z0-9. -]*$/i.test(value || '')
    ? undefined
    : 'Only alphanumeric characters, spaces, dots and hyphens';

export const alphanumericWithHyphen = (value) =>
  /^$|^[a-zA-Z0-9][a-zA-Z0-9-]*$/i.test(value || '')
    ? undefined
    : 'Only alphanumeric characters and hyphens are allowed';

export const alphanumericWithHyphenAndSpace = (value) =>
  /^$|^[a-zA-Z0-9][a-zA-Z0-9- ]*$/i.test(value || '')
    ? undefined
    : 'Only alphanumeric characters, spaces and hyphens are allowed';

export const alphanumericWithParenthesis = (value) =>
  /^$|^[a-zA-Z0-9][a-zA-Z0-9()]*$/i.test(value || '') ? undefined : 'Only alphanumeric ( and )';

export const alphaNumStartWithTOrS = (value) =>
  /^$|^[st][a-zA-Z0-9][a-zA-Z0-9()]*$/i.test(value || '')
    ? undefined
    : 'First letter should start with either S or T';

/** This Regex will allow starts with M also as SG has a new series of FIN */
export const alphaNumStartWithGOrF = (value) =>
  /^$|^[fgm][a-zA-Z0-9][a-zA-Z0-9()]*$/i.test(value || '')
    ? undefined
    : 'First letter should start with either F or G or M';

/*export const alphaNumericWithSpaceCommaDot = value =>
  value && /^[a-zA-Z0-9][a-zA-Z0-9., ]*$/i.test(value) ?
    undefined : 'Only alphanumeric characters';*/

export const alphanumericWithSpecialCharacters = (value) =>
  value && /^$|^[a-zA-Z0-9.#,-/\\\s]*$/i.test(value) ? undefined : 'Invalid Text';

export const address = (value) =>
  value && /^$|^[a-zA-Z0-9.#&*,-/\\\s]*$/i.test(value) ? undefined : 'Invalid address';

export const url = (value) =>
  /^$|^(http[s]?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}[.]?/i.test(value || '')
    ? undefined
    : 'Invalid URL';

export const alphabetsAndNumbersOnly = (value) =>
  /^$|^[a-zA-Z0-9]*$/i.test(value) ? undefined : 'Only alphabets and numbers allowed';

export const couponCodeValidation = (value) =>
  alphabetsAndNumbersOnly(value) && 'Invalid Coupon Code';

export const pan_validation = (value) => {
  if (value && /^[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}$/g.test(value)) {
    return undefined;
  } else {
    return 'Please enter a valid PAN';
  }
};

export const validateGSTNumber = (value) => {
  if (
    value === undefined ||
    value === '' ||
    (value && /^[0-9]{2}[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[0-9]{1}[a-zA-Z]{1}[0-9]{1}$/g.test(value))
  ) {
    return undefined;
  } else {
    return 'Invalid GST number.';
  }
};

export const first_lastNameValidation =
  (_type, isRequired = true) =>
  (value) => {
    const trimmedValue = (value || '').trim();

    // Condition 1: Empty → Required
    if (isRequired && !trimmedValue) {
      return 'Required';
    }

    if (!trimmedValue) return undefined;

    // Condition 2/4: Less than 2 characters
    if (!BENE_NAME_MIN_LENGTH_REGEX.test(trimmedValue)) {
      return 'Please enter the full name. Initials are not allowed.';
    }

    // Condition 3: Special characters present
    if (BENE_NAME_SPECIAL_CHAR_REGEX.test(trimmedValue)) {
      return 'Please enter a valid name without any special characters.';
    }

    return undefined;
  };

export const middlenameValidation = (value) => {
  //zero length validation is handled by various minLength
  let newVal = value || '';
  let validation = minLength2(newVal);
  //if minlength validation passes we check for alphanumeric validation
  !validation && (validation = alphaWithDotAndSpaceAndHyphen(newVal));
  return (
    validation &&
    'Your middle name must contain at least 2 characters, no special characters and no numbers'
  );
};

export const phoneNumber = (value) =>
  value && !/^(0|[1-9][0-9]{9})$/i.test(value)
    ? 'Invalid phone number, must be 10 digits'
    : undefined;

export const auMobValidation = (value) =>
  value && !/^(4)([0-9]{8})$/i.test(value)
    ? value.length !== 9
      ? 'Your mobile number must contain exactly 9 digits'
      : 'Your mobile number must begin with 4'
    : undefined;

export const passwordValidation = (value = '') => {
  let error =
    value &&
    // eslint-disable-next-line max-len
    !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,}$/g.test(
      value
    )
      ? ' '
      : undefined;
  if (value.length < 8) error = ' ';
  return error;
};

export const hkidValidation = (value = '') =>
  /^[A-Z]{1,2}[0-9]{6}[(][0-9A]{1}[)]$/g.test(value)
    ? undefined
    : 'HK Id number not in the correct format';

export const passportValidationAU = (value, allValues) => {
  if (allValues && allValues.PASSPORT && allValues.PASSPORT.country_of_issue === 'AUS') {
    return !value || /^[A-Z]{1,2}[0-9]{7}$/g.test(value)
      ? undefined
      : 'Passport number not in the correct format';
  } else {
    return alphanumeric(value);
  }
};

// empty also checks for empty string
const isEmpty = (value) => value === undefined || value === null || value === '';

// Check if user has entered a value
export const isRequired = (value) => isEmpty(value);

// no special characters
export const noSpecialChar = (value) =>
  /^$|^[a-zA-Z0-9]*$/g.test(value) ? undefined : 'No special characters';

// ony text
export const textOnly = (value) => (/^[a-zA-Z]*$/i.test(value) ? undefined : 'Only alphabets');

export const textWithSpacesOnly = (value) => {
  if (value) {
    if (/^[a-zA-Z ]*$/i.test(value)) {
      return undefined;
    } else {
      return 'Only alphabets';
    }
  } else {
    return undefined;
  }
};

export const textWithLimitedCharacters = (value) => {
  if (value) {
    if (/^[a-zA-Z ',-]*$/i.test(value)) {
      return undefined;
    } else {
      return 'Only alphabets, apostrophe, hyphen and comma allowed';
    }
  } else {
    return undefined;
  }
};

// required date should be greater than today
export const greaterThanToday = (value) =>
  value <= new Date() ? 'Should be greater than today' : undefined;

// required date should be less than today
export const lessThanToday = (value) =>
  value > new Date() ? 'Should be less than today' : undefined;

export const dateOfBirth = (value) => {
  const dob = moment(value, 'DD/MM/YYYY');
  const today = moment(new Date());
  let verify = today.diff(dob, 'years', true);
  if (verify >= 18) {
    return undefined;
  } else {
    return `Should be greater than 18 years`;
  }
};

export const dateOfBirthUS = (value) => {
  const dob = moment(value, 'MM/DD/YYYY');
  const today = moment(new Date());
  let verify = today.diff(dob, 'years', true);
  if (verify >= 18) {
    return undefined;
  } else {
    return `Should be greater than 18 years`;
  }
};

export const isValidDateFormat = (str) => {
  let returnval = undefined;
  if (str == null || !str.isValid()) {
    returnval = 'Invalid Date';
  }
  return returnval;
};

export const isValidDate = (value) => {
  return moment(value).isValid() ? undefined : 'must be a valid Date';
};

export const regexValidation = (pattern, message) => (value) => {
  if (!value) return undefined;
  return new RegExp(pattern).test(value) ? undefined : message || 'Invalid format';
};

export const exactLength = (exact, message) => {
  return function (value) {
    if (!value) return undefined;
    if (String(value).length === exact) {
      return undefined;
    } else {
      return message || `Should be ${exact} digits exact`;
    }
  };
};

export const exactLength4 = exactLength(4);
export const exactLength10 = exactLength(10);
export const exactLength16 = exactLength(16);
export const exactLength18 = exactLength(18);
export const exactLength3 = exactLength(3);

export const validateAmazeTransactionNumber = (value) => {
  const isMinLength6 = value && minLength(6)(value);
  const isMaxLength15 = value && maxLength(15)(value);
  if (isMinLength6 || isMaxLength15) return `Invalid transaction number`;
  else return undefined;
};

export const validateRemittanceTransactionNumber = exactLength(10, `Invalid transaction number`);

export const lessThanMonths = (max) => {
  return (value) => {
    const enteredDate = moment(value);
    if (enteredDate <= moment().subtract(max, 'months')) {
      return undefined;
    } else {
      return `Should be less than ${max} months`;
    }
  };
};

export const greaterThanMonths = (min) => {
  return (value) => {
    const enteredDate = moment(value);
    const today = moment(new Date());
    if (today.diff(enteredDate, 'months', true) >= min) {
      return undefined;
    } else {
      return `Should be greater than ${min} months`;
    }
  };
};

export const lessThanThreeMonths = lessThanMonths(3);

export const exactLength9 = exactLength(9);

export const lengthOr = (value, pArray) => {
  let found = false;
  pArray.forEach((item) => {
    if (value.length === parseInt(item, 10)) {
      found = true;
    }
  });
  if (found) {
    return undefined;
  } else {
    return 'Length should be ' + pArray.join(' or ');
  }
};

export const validateRoutingValue = (params, url, method) => {
  let options = {
    url: `${API_URL}/${url}`,
    method: method || 'GET',
    withCredentials: true
  };
  if (options.method.toUpperCase() === 'GET') {
    options.params = params;
  } else {
    options.data = params;
  }
  return makeApiCall(options);
};

export const validateReferralCode = (value) => {
  if (
    value === undefined ||
    (minLength(6)(value) === undefined &&
      alphanumeric(value) === undefined &&
      maxLength(20)(value) === undefined)
  ) {
    return undefined;
  } else {
    return 'Invalid referral code.';
  }
};

export function dynamic_validator(key, values, validationObj, errors) {
  if (values === undefined) return;

  if (validationObj) {
    if (validationObj.required && validationObj.required === true) {
      // for required
      if (!values[key]) {
        errors[key] = 'Required';
      }
    }

    if (validationObj.type && values[key]) {
      // for type i.e. alphanumeric, textOnly, number
      let type = validationObj.type;
      const pattern = validationObj.pattern;
      let error;
      switch (type) {
        case 'text':
          error = textWithLimitedCharacters(values[key]);
          errors[key] = error ? error : errors[key];
          break;
        case 'number':
          error = number(values[key]);
          errors[key] = error ? error : errors[key];
          break;
        case 'allString':
          error = allString(values[key]);
          errors[key] = error ? error : errors[key];
          break;
        case 'alphanumeric':
          error = alphanumeric(values[key]);
          errors[key] = error ? error : errors[key];
          break;
        case 'alphanumericWithSpaceAndDot':
          error = alphanumericWithSpaceAndDot(values[key]);
          errors[key] = error ? error : errors[key];
          break;
        case 'alphanumericWithSpaceDotAndHyphen':
          error = alphanumericWithSpaceDotAndHyphen(values[key]);
          errors[key] = error ? error : errors[key];
          break;
        case 'alphanumericWithParenthesis':
          error = alphanumericWithParenthesis(values[key]);
          errors[key] = error ? error : errors[key];
          break;
        case 'iban': {
          // check for IBAN format
          const account_number = values[key]; // from the input form
          const prefix = account_number.substring(0, 2);
          if (validationObj.country_code && validationObj.country_code !== prefix) {
            errors[key] = 'Invalid Bank Account number';
          }
          break;
        }
        case 'pattern': {
          if (pattern) {
            const regex = new RegExp(pattern);
            error = regex.test(values[key] || '') ? undefined : validationObj.error_message || '';
            errors[key] = error ? error : errors[key];
          }
          break;
        }
        default:
          break;
      }
    }

    if (validationObj.length && values[key]) {
      // for length
      const lengthObj = validationObj.length;
      if (lengthObj.min && values[key].length < lengthObj.min) {
        errors[key] = `Length must not be less than ${lengthObj.min}`;
      }
      if (lengthObj.max && values[key].length > lengthObj.max) {
        errors[key] = `Length must not be more than ${lengthObj.max}`;
      }
      if (lengthObj.exact) {
        if (
          lengthObj.exact instanceof Array &&
          lengthObj.exact.indexOf(values[key].length) === -1
        ) {
          errors[key] = `Length must be ${lengthObj.exact.join(',')}`;
        } else if (typeof lengthObj.exact === 'number' && lengthObj.exact !== values[key].length) {
          errors[key] = `Length must be ${lengthObj.exact}`;
        }
      }
    }

    return errors;
  }
}

export function dynamic_validator2(value, validationObj, errors, options) {
  if (validationObj) {
    //required validation
    let isFieldReq = false;
    let isCorp = false;
    if (options) {
      let { allValues, name } = options;
      isCorp = options.isCorp;
      if (isCorp) isFieldReq = checkFieldInFormSection(allValues, name, 2, 'doc_file');
    }

    if (isCorp) {
      if (isFieldReq && validationObj.required && validationObj.required === true && !value) {
        return 'Required';
      } else if (!value) {
        return undefined;
      }
    } else if (validationObj.required && validationObj.required === true && !value) {
      return 'Required';
    }

    let error = null;
    if (validationObj.noSpecialChars && validationObj.noSpecialChars === true) {
      // eslint-disable-next-line
      if ((error = noSpecialChar(value))) {
        return error;
      }
    }

    let err = undefined;
    //text, number, string, alphanumeric, etc validations
    if (validationObj.type && value) {
      let validateType = validationObj.type;
      const pattern = validationObj.pattern;
      switch (validateType) {
        case 'text':
          err = textWithLimitedCharacters(value);
          break;
        case 'number':
        case 'numeric':
          err = number(value);
          break;
        case 'allString':
          err = allString(value);
          break;
        case 'alphanumeric':
          err = alphanumeric(value);
          break;
        case 'alphanumericWithSpace':
          err = alphanumericWithSpace(value);
          break;
        case 'alphanumericWithSpaceAndDot':
          err = alphanumericWithSpaceAndDot(value);
          break;
        case 'alphanumericWithDotAndSpace':
          err = alphanumericWithSpaceAndDot(value);
          break;
        case 'alphanumericWithSpaceDotAndHyphen':
          err = alphanumericWithSpaceDotAndHyphen(value);
          break;
        case 'alphanumericWithParenthesis':
          err = alphanumericWithParenthesis(value);
          break;
        case 'pan_validation':
          err = pan_validation(value);
          break;
        case 'alphaNumStartWithTOrS':
          err = alphaNumStartWithTOrS(value);
          break;
        case 'alphaNumStartWithGOrF':
          err = alphaNumStartWithGOrF(value);
          break;
        case 'hkidValidation':
          err = hkidValidation(value);
          break;
        case 'email':
        case 'string': {
          if (pattern) {
            const regex = new RegExp(pattern);
            err = regex.test(value || '') ? undefined : validationObj.error_message || '';
          }
          break;
        }

        default:
          break;
      }
      if (err) return err;

      //date picker validation
      if (validationObj.type === 'date_picker' && Object.keys(validationObj.range).length > 0) {
        let { range } = validationObj;
        if (range && range.min_identifier) {
          if (value < moment().subtract(Math.abs(range.min_offset) + 1, range.min_identifier)) {
            if (range.min_offset === 0) return `Should be later than today`;
            else
              return `Should be later than ${Math.abs(range.min_offset)} ${range.min_identifier}`;
          }
        }
        if (range && range.max_identifier) {
          if (value > moment().add(Math.abs(range.max_offset), range.max_identifier)) {
            if (range.max_offset === 0) return `Should be earlier than today`;
            else
              return `Should be earlier than ${Math.abs(range.max_offset)} ${range.max_identifier}`;
          }
        }
        if (value === 'Invalid date') {
          return `Invalid Date Format`;
        }
      }
    }

    //length validation
    if (validationObj?.length) {
      const lengthObj = validationObj.length;
      if (lengthObj.min && value?.length < lengthObj.min) {
        return `Length must not be less than ${lengthObj.min}`;
      }
      if (lengthObj.max && value?.length > lengthObj.max) {
        return `Length must not be more than ${lengthObj.max}`;
      }
      if (lengthObj.exact) {
        if (lengthObj.exact instanceof Array && lengthObj.exact.indexOf(value?.length) === -1) {
          return `Length must be ${lengthObj.exact.join(',')}`;
        } else if (typeof lengthObj.exact === 'number' && lengthObj.exact !== value.length) {
          return `Length must be ${lengthObj.exact}`;
        }
      }
    }
    return err;
  }
}

export const comparePassword = (one, two) => {
  var value1, value2;
  Object.keys(two).forEach((key) => {
    if (key === 'password' || key === 'new_password') {
      value1 = two[key];
    }
  });
  Object.keys(two).forEach((key) => {
    if (key === 'confirm_password' || key === 'confirm_new_password') {
      value2 = two[key];
    }
  });
  if (value1 && value2 && value1 === value2) {
    return undefined;
  } else {
    return 'passwords do not match';
  }
};

export const compareAccountNumber = (one, two) => {
  var value1, value2;
  Object.keys(two).forEach((key) => {
    if (key === 'bank_account_number') {
      value1 = two[key];
      return;
    }
  });
  Object.keys(two).forEach((key) => {
    if (key === 're_enter_account_number') {
      value2 = two[key];
      return;
    }
  });
  if (value1 && value2 && value1 === value2) {
    return undefined;
  } else {
    return 'Account Numbers Do Not Match';
  }
};

export const requiredWithoutZero = (value) => {
  if (value || value === 0) {
    return undefined;
  } else {
    return 'Required';
  }
};

export const greaterThanZero = (value) =>
  Number(value) > 0 ? undefined : 'Amount should be greater than 0';
export const equall12Aadhar = (value) =>
  value.length !== 12 ? 'Please enter 12 digit Aadhar number' : undefined;

export const fileSizeCheckInMB = (size, limit) => {
  if (size && limit) {
    if (size / 1000 / 1000 > limit) {
      return `File size should not be more than ${limit} MB`;
    } else return undefined;
  } else return 'Required';
};

export const dynamicMobileValidation = (value, allValues, props) => {
  let { mobileValidationReducer } = props,
    errors,
    newValue = value || '';
  const isUsUSDBenePage =
    props.bank_account_country === 'US' &&
    props.currency_code === 'USD' &&
    props.form === 'payeeDetails';
  if (mobileValidationReducer) {
    if (mobileValidationReducer.required) {
      errors = required(newValue);
      if (errors) return errors;
    }
    if (mobileValidationReducer.type && mobileValidationReducer.type === 'number') {
      errors = number(newValue);
      if (errors) return errors;
    }
    if (mobileValidationReducer.length) {
      let { min, exact } = mobileValidationReducer.length;
      !errors &&
        min &&
        (errors =
          newValue.length < min && `Your mobile number must contain at least ${min} digits`);
      !errors &&
        exact &&
        (errors =
          newValue.length < exact && `Your mobile number must contain exactly ${exact} digits`);
      return errors;
    }
  } else if (props.mobileValidation) {
    if (!isUsUSDBenePage) {
      errors = required(newValue);
    }
    if (errors) return errors;
    if (props.mobileValidation.min && newValue) {
      const { min } = props.mobileValidation;
      // eslint-disable-next-line no-irregular-whitespace
      errors = newValue.length < min && `Your mobile number must contain at least ${min} digits`;
    }
    if (!errors && props.mobileValidation.exact && newValue) {
      const { exact } = props.mobileValidation;
      // eslint-disable-next-line no-irregular-whitespace
      errors = newValue.length < exact && `Your mobile number must contain exactly ${exact} digits`;
    }
  }

  return errors;
};

export const getMimetype = (signature) => {
  switch (signature) {
    case '89504E47':
      return 'image/png';
    case '47494638':
      return 'image/gif';
    case '25504446':
      return 'application/pdf';
    case 'FFD8FFDB':
    case 'FFD8FFE0':
    case 'FFD8FFE1':
      return 'image/jpeg';
    case '504B0304':
      return 'application/zip';
    default:
      return 'Unknown filetype';
  }
};

export const dependentValidation = (val, allVal, p, name = '') => {
  let nameArr = name.split('.');
  if (nameArr && nameArr[0] && allVal && allVal[nameArr[0]] && !val) {
    return 'Please complete all relevant fields';
  }
};

export const cardExpiryValidation = (value) => {
  if (value) {
    let today = new Date();
    let CurrentDate = moment(
      new Date(
        today.getFullYear() +
          '-' +
          (today.getMonth() + 1) +
          '-' +
          new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
      )
    );
    let visaValue = value.split('-');
    let visaDate = new Date(visaValue[0], visaValue[1], 0).getDate();
    return value && /^[0-9]{4}-(0[1-9]|1[0-2])$/i.test(value.trim())
      ? CurrentDate < moment(value + '-' + visaDate)
        ? undefined
        : 'Please enter valid date'
      : 'Invalid date format';
  }
};
export const visaDebitCardNoValidation = (value) => {
  if (value) {
    return value && /^4[0-9]{15}$/i.test(value.trim()) ? '' : 'Enter a valid Visa debit card';
  }
};

export const stripeCardExpirValidation = (value) => {
  if (value) {
    if (/^(0[1-9]|1[0-2])\/[0-9]{2}$/i.test(value.trim())) {
      const cardExpiryDate = moment(value, 'MM/YY');
      const isDateValid = moment(cardExpiryDate).isAfter(moment().startOf('month'));
      return isDateValid ? undefined : 'Please enter valid date';
    } else {
      return 'Invalid date format';
    }
  }
};

export function stripeCardNumberValidation(cardNumber) {
  const regexPattern = {
    MASTERCARD: /^5[1-5][0-9]{1,}|^2[2-7][0-9]{1,}$/,
    VISA: /^4[0-9]{2,}$/
  };
  for (const card in regexPattern) {
    if (cardNumber.replace(/[^\d]/g, '').match(regexPattern[card])) return '';
  }
  return 'We only accept Visa and Mastercard';
}

export const validatePattern = (validationObj) => (value) => {
  const globalRegex = new RegExp(validationObj.pattern, 'g');
  return globalRegex.test(value || '') ? undefined : validationObj.error_message;
};

export const validateNiumForexTransactionNumber = exactLength(12, `Invalid transaction number`);

export {
  showInvoiceNumberAndDates,
  r_password,
  r_maxLengthWith2DigitsAfterDecimal,
  r_maxLength,
  r_upperCase,
  r_mobileNumber,
  phone_length_by_country,
  beneSchemaValidation,
  bank_validations,
  r_onlyNumbers,
  r_onlyCaps,
  r_greaterThan,
  r_lessThan,
  r_phoneNumber,
  r_alphaNumeric,
  no_spaces,
  normalizeAll,
  normalizeCurrency,
  dynamic_normalizer
};

export const validateAddressForm = (values, props) => {
  const errors = {};

  if (props?.countryCode === 'GB') {
    const isBuildingNameEmpty = !values?.building_name?.trim();
    const isBuildingNumberEmpty = !values?.building_number?.trim();

    if (isBuildingNameEmpty && isBuildingNumberEmpty) {
      errors.building_name = 'Please provide one of Building no. or Building name';
    }
  }

  return errors;
};
